import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { cn, hasText } from "@/lib/utils";
import CustomSwiper from "./CustomSwiper";
import VariableImage from "./ui/VariableImage";

gsap.registerPlugin(useGSAP, ScrollTrigger);


const DrawerSectionDesktop = ({
  mainTitle,
  title,
  text,
  time,
  img,
  slidesTitle,
  slides,
  logo,
}: {
  mainTitle: string;
  title: string;
  text: string;
  time: any[];
  img: {
    url: string;
    alternativeText: string;
    caption: string;
  };
  slidesTitle: string;
  slides: any[];
  logo?: {
    url: string;
    alternativeText: string;
    caption: string;
  };
}) => {
  const t = useTranslations("global");
  const [featured, setFeatured] = useState(t("view"));
  const sectionRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const handleFeature = () => {
    setFeatured((prev) => (prev === t("view") ? t("close") : t("view")));
  };

  useGSAP(
    () => {
      gsap.from(sectionRef.current, {
        y: 50,
        autoAlpha: 0,
        duration: 1,
        ease: "power2.inOut",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
        },
      });
    },
    { scope: sectionRef },
  );

  return (
    <div ref={sectionRef} className="relative">
      <h2 className="title-70 container mb-10 font-semibold normal-case">
        {mainTitle}
      </h2>

      <div className="group relative mx-auto mb-20 flex aspect-16/7 w-full max-w-[calc(100%-40px)] flex-wrap overflow-hidden">
        <div className="blur-custom absolute bottom-0 z-8 flex h-full w-1/2 flex-col items-start px-5 py-10 text-white lg:p-12 2xl:p-20">
          <h3 className="title-30 capitalize! font-semibold leading-[120%]!">
            {title}
          </h3>
          <div className="mt-auto flex flex-col gap-4">
            <p className="whitespace-pre-line text-lg">{text}</p>
            <div className="text-sm capitalize">
              {time?.map((item: any) => (
                <p key={item.id}>{item.Text}</p>
              ))}
            </div>
            {slides.length > 1 && (
              <button
                className="w-fit capitalize underline"
                onClick={() => {
                  handleFeature();
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: "cta_button_click",
                    button_name: `${featured} ${t("featuredDishes")}`,
                    section: mainTitle,
                  });
                }}
              >
                {featured} {t("featuredDishes")}
              </button>
            )}
          </div>
        </div>

        <div className="absolute inset-0 size-full transition-transform duration-1000 ease-in-out will-change-auto group-hover:scale-110">
          <VariableImage
            src={img.url}
            width={1920}
            height={1080}
            className="size-full object-cover object-center rtl:-scale-x-100"
            alt={img.alternativeText}
            caption={img.caption}
          />
        </div>

        {logo && (
          <VariableImage
            src={logo.url}
            width={170}
            height={150}
            className="absolute inset-e-5 top-5 h-[50px] w-[150px] object-contain"
            alt={logo.alternativeText}
            caption={logo.caption}
          />
        )}
      </div>

      <div
        ref={sliderRef}
        className={cn(
          "relative h-0 overflow-hidden px-5 opacity-0 transition-all duration-500 ease-in-out will-change-auto",
          featured === t("close")
            ? "h-[650px] opacity-100"
            : "pointer-events-none",
        )}
      >
        {hasText(slidesTitle) && (
          <h2 className="title-30 capitalize! mb-10 font-semibold">{slidesTitle}</h2>
        )}
        {slides.length > 1 && <CustomSwiper slides={slides} />}
      </div>
    </div>
  );
};

export default DrawerSectionDesktop;
